import { supabase } from '../lib/supabase';

/**
 * Service for managing teams and team accounts
 * Handles team creation, member management, invitations, and team analytics
 */
export const teamService = {
  /**
   * Get all teams for current user (owned or member of)
   * @returns {Promise<Array>} Array of teams
   */
  async getUserTeams() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('teams')
        ?.select(`
          *,
          team_members(id, user_id, role)
        `)
        ?.or(`owner_id.eq.${user.id},team_members.user_id.eq.${user.id}`);

      if (error) throw error;

      return data?.map(team => ({
        id: team?.id,
        name: team?.name,
        slug: team?.slug,
        description: team?.description,
        accountType: team?.account_type,
        ownerId: team?.owner_id,
        logoUrl: team?.logo_url,
        subscriptionTier: team?.subscription_tier,
        maxMembers: team?.max_members,
        activeMembers: team?.active_members,
        createdAt: team?.created_at,
        updatedAt: team?.updated_at,
        members: team?.team_members
      })) || [];
    } catch (error) {
      console.error('Error fetching user teams:', error);
      throw error;
    }
  },

  /**
   * Get team details with all members
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Team data with members
   */
  async getTeamDetails(teamId) {
    try {
      const { data, error } = await supabase
        ?.from('teams')
        ?.select(`
          *,
          team_members(
            id,
            user_id,
            role,
            joined_at,
            last_activity_at
          )
        `)
        ?.eq('id', teamId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        slug: data?.slug,
        description: data?.description,
        accountType: data?.account_type,
        ownerId: data?.owner_id,
        logoUrl: data?.logo_url,
        subscriptionTier: data?.subscription_tier,
        maxMembers: data?.max_members,
        activeMembers: data?.active_members,
        members: data?.team_members?.map(m => ({
          id: m?.id,
          userId: m?.user_id,
          role: m?.role,
          joinedAt: m?.joined_at,
          lastActivityAt: m?.last_activity_at
        })),
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching team details:', error);
      throw error;
    }
  },

  /**
   * Create a new team
   * @param {Object} teamData - Team data (name, description, etc.)
   * @returns {Promise<Object>} Created team
   */
  async createTeam(teamData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const slug = teamData?.name
        ?.toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '') + '-' + Math.random().toString(36).substring(7);

      const { data, error } = await supabase
        ?.from('teams')
        ?.insert({
          name: teamData?.name,
          slug,
          description: teamData?.description,
          account_type: teamData?.accountType || 'team',
          owner_id: user?.id,
          max_members: teamData?.maxMembers || 10,
          subscription_tier: teamData?.subscriptionTier || 'free'
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Add owner as team member
      await supabase
        ?.from('team_members')
        ?.insert({
          team_id: data?.id,
          user_id: user?.id,
          role: 'owner',
          invitation_status: 'active'
        });

      return {
        id: data?.id,
        name: data?.name,
        slug: data?.slug,
        description: data?.description,
        accountType: data?.account_type,
        ownerId: data?.owner_id,
        subscriptionTier: data?.subscription_tier,
        maxMembers: data?.max_members,
        activeMembers: 1,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating team:', error);
      throw error;
    }
  },

  /**
   * Invite user to team
   * @param {string} teamId - Team ID
   * @param {string} email - Email of user to invite
   * @param {string} role - Role to assign (member, manager)
   * @returns {Promise<Object>} Invitation details
   */
  async inviteTeamMember(teamId, email, role = 'member') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate invitation token
      const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(); // 7 days

      const { data, error } = await supabase
        ?.from('team_invitations')
        ?.insert({
          team_id: teamId,
          invited_by_id: user?.id,
          email,
          role,
          token,
          expires_at: expiresAt
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        teamId: data?.team_id,
        email: data?.email,
        role: data?.role,
        token: data?.token,
        expiresAt: data?.expires_at,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error inviting team member:', error);
      throw error;
    }
  },

  /**
   * Accept team invitation
   * @param {string} token - Invitation token
   * @returns {Promise<Object>} Team membership details
   */
  async acceptInvitation(token) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get invitation
      const { data: invitation, error: invError } = await supabase
        ?.from('team_invitations')
        ?.select('*')
        ?.eq('token', token)
        ?.single();

      if (invError || !invitation) throw new Error('Invalid or expired invitation');
      if (new Date(invitation?.expires_at) < new Date()) throw new Error('Invitation expired');

      // Add user to team
      const { data: membership, error: memberError } = await supabase
        ?.from('team_members')
        ?.insert({
          team_id: invitation?.team_id,
          user_id: user?.id,
          role: invitation?.role,
          invitation_status: 'active'
        })
        ?.select()
        ?.single();

      if (memberError) throw memberError;

      // Mark invitation as accepted
      await supabase
        ?.from('team_invitations')
        ?.update({ accepted_at: new Date().toISOString() })
        ?.eq('id', invitation?.id);

      // Update user's primary team if first team
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('primary_team_id')
        ?.eq('id', user?.id)
        ?.single();

      if (!profile?.primary_team_id) {
        await supabase
          ?.from('user_profiles')
          ?.update({ primary_team_id: invitation?.team_id })
          ?.eq('id', user?.id);
      }

      return {
        id: membership?.id,
        teamId: membership?.team_id,
        userId: membership?.user_id,
        role: membership?.role,
        joinedAt: membership?.joined_at
      };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  },

  /**
   * Remove team member
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID to remove
   * @returns {Promise<boolean>} Success status
   */
  async removeTeamMember(teamId, userId) {
    try {
      const { error } = await supabase
        ?.from('team_members')
        ?.delete()
        ?.eq('team_id', teamId)
        ?.eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing team member:', error);
      throw error;
    }
  },

  /**
   * Update team member role
   * @param {string} teamId - Team ID
   * @param {string} userId - User ID
   * @param {string} role - New role (owner, manager, member)
   * @returns {Promise<Object>} Updated membership
   */
  async updateTeamMemberRole(teamId, userId, role) {
    try {
      const { data, error } = await supabase
        ?.from('team_members')
        ?.update({ role })
        ?.eq('team_id', teamId)
        ?.eq('user_id', userId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        teamId: data?.team_id,
        userId: data?.user_id,
        role: data?.role
      };
    } catch (error) {
      console.error('Error updating team member role:', error);
      throw error;
    }
  },

  /**
   * Get team analytics
   * @param {string} teamId - Team ID
   * @param {string} dateRange - 'today', 'week', 'month', 'all'
   * @returns {Promise<Object>} Team analytics
   */
  async getTeamAnalytics(teamId, dateRange = 'week') {
    try {
      let query = supabase
        ?.from('team_analytics')
        ?.select('*')
        ?.eq('team_id', teamId)
        ?.order('date', { ascending: false });

      // Filter by date range
      if (dateRange === 'today') {
        query = query?.eq('date', new Date().toISOString().split('T')[0]);
      } else if (dateRange === 'week') {
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        query = query?.gte('date', weekAgo.toISOString().split('T')[0]);
      } else if (dateRange === 'month') {
        const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        query = query?.gte('date', monthAgo.toISOString().split('T')[0]);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(record => ({
        id: record?.id,
        teamId: record?.team_id,
        date: record?.date,
        totalDrillsCompleted: record?.total_drills_completed,
        totalObjectionsPracticed: record?.total_objections_practiced,
        avgDrillScore: record?.avg_drill_score,
        avgObjectionScore: record?.avg_objection_score,
        activeMembers: record?.active_members,
        totalXpEarned: record?.total_xp_earned
      })) || [];
    } catch (error) {
      console.error('Error fetching team analytics:', error);
      throw error;
    }
  },

  /**
   * Get per-member analytics for a team
   * @param {string} teamId - Team ID
   * @param {string} date - Optional specific date (YYYY-MM-DD)
   * @returns {Promise<Array>} Member analytics
   */
  async getTeamMemberAnalytics(teamId, date = null) {
    try {
      let query = supabase
        ?.from('team_member_analytics')
        ?.select(`
          *,
          user:user_id(full_name, email)
        `)
        ?.eq('team_id', teamId);

      if (date) {
        query = query?.eq('date', date);
      }

      query = query?.order('xp_earned', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(record => ({
        id: record?.id,
        teamId: record?.team_id,
        userId: record?.user_id,
        date: record?.date,
        drillsCompleted: record?.drills_completed,
        objectionsCompleted: record?.objections_practiced,
        avgDrillScore: record?.avg_drill_score,
        avgObjectionScore: record?.avg_objection_score,
        xpEarned: record?.xp_earned,
        streakDays: record?.streak_days,
        user: {
          fullName: record?.user?.full_name,
          email: record?.user?.email
        }
      })) || [];
    } catch (error) {
      console.error('Error fetching team member analytics:', error);
      throw error;
    }
  },

  /**
   * Update team settings
   * @param {string} teamId - Team ID
   * @param {Object} settings - Settings to update
   * @returns {Promise<Object>} Updated team
   */
  async updateTeamSettings(teamId, settings) {
    try {
      const { data, error } = await supabase
        ?.from('teams')
        ?.update({ settings })
        ?.eq('id', teamId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        name: data?.name,
        settings: data?.settings
      };
    } catch (error) {
      console.error('Error updating team settings:', error);
      throw error;
    }
  },

  /**
   * Upgrade team subscription
   * @param {string} teamId - Team ID
   * @param {string} tier - Subscription tier (free, pro, enterprise)
   * @param {number} maxMembers - Max members for tier
   * @returns {Promise<Object>} Updated team
   */
  async upgradeTeamSubscription(teamId, tier, maxMembers) {
    try {
      const { data, error } = await supabase
        ?.from('teams')
        ?.update({
          subscription_tier: tier,
          max_members: maxMembers
        })
        ?.eq('id', teamId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        subscriptionTier: data?.subscription_tier,
        maxMembers: data?.max_members
      };
    } catch (error) {
      console.error('Error upgrading team subscription:', error);
      throw error;
    }
  }
};
